/* Copyright 2017-2020 The MathWorks, Inc. */

/* eslint-disable camelcase, no-unused-vars */

define([
    'mw-messageservice/MessageService',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/dom-construct',
    'dojo/dom-class',
    'dojo/dom',
    'dojo/dom-attr',
    'dojo/dom-style',
    'dojo/html',
    'dojo/store/Memory',
    'dojo/store/Observable',
    'dijit/layout/BorderContainer',
    'dojox/layout/TableContainer',
    'dijit/layout/ContentPane',
    'dijit/ProgressBar',
    'BuildStatus/ModelStatusList',
    'dojo/i18n!BuildStatus/resources/RTW/nls/buildStatus',
    'dojo/domReady!'
], function (MessageService, declare, lang, domConstruct, domClass, dom, domAttr, domStyle, domHTML,
    Memory, Observable, BorderContainer, TableContainer, ContentPane, ProgressBar,
    ModelStatusList, appL10n) {
    var BuildStatusInfo = declare(null, {
        constructor: function (model, targetType, initialMsg) {
            this.model = model;
            this.targetType = targetType;

            var channelPrefix = '/BuildStatusUI/' + this.model + '/' + this.targetType;
            this.informReadyChannel = channelPrefix + '/informReady';
            this.updateBuildStatusTableChannel = channelPrefix + '/updateBuildStatusTable';
            this.topProgressBarChannel = channelPrefix + '/topProgressBar';
            this.poolUsageChannel = channelPrefix + '/poolUsage';

            this.numWks = 0;
            this.numTotalMdls = 0;
            this._stopTimer = false;

            MessageService.start();

            // -----------------------------------------------------------------------------
            // create a border container for the main layout
            var mainContainer = new BorderContainer({
                gutters: false,
                liveSplitters: false,
                region: 'center',
                style: 'height:90%; width:100%; padding:5px;'
            });

            // -----------------------------------------------------------------------------
            // Create the top overall progress text and progress bar
            // create a border container for the top
            var headerContainer = new BorderContainer({
                gutters: false,
                liveSplitters: false,
                region: 'top',
                style: 'height:90px; width:100%; margin:0;'
            });
            mainContainer.addChild(headerContainer);

            var headerLeft = new ContentPane({
                region: 'left',
                splitter: false,
                style: 'border:none;'
            });
            headerContainer.addChild(headerLeft);

            var headerCenter = new ContentPane({
                region: 'center',
                splitter: false,
                style: 'height:100%; width:100%; border:none;'
            });
            headerContainer.addChild(headerCenter);

            this.topProgressBar = new ProgressBar({
                value: 0,
                align: 'center'
            });
            domClass.add(this.topProgressBar.domNode, 'topProgressBar');
            headerCenter.addChild(this.topProgressBar);

            var overallProgressText = domConstruct.create('div', {
                style: { 'padding-top': '45px',
                    'font-weight': 'bold',
                    'font-size': '10pt',
                    'text-align': 'center' },
                innerHTML: appL10n.OverallProgressText
            }, headerCenter.containerNode);

            domAttr.set(this.topProgressBar, 'data-tag', 'build-status.' + this.targetType + '.topProgressBar');

            // -----------------------------------------------------------------------------
            // Create a container for summary info
            var statusTable = new TableContainer({
                cols: 1,
                orientation: 'horiz',
                customClass: 'statusTable'
            });

            this.elapsedTimeHeader = new ContentPane({
                label: appL10n.ElapsedTime,
                readOnly: true
            });

            this.workerInUse = new ContentPane({
                label: appL10n.NumActiveWorkers,
                readOnly: true
            });

            this.optNumWorkers = new ContentPane({
                label: appL10n.OptPoolSize,
                content: appL10n.Pending,
                readOnly: true
            });
            domClass.add(this.optNumWorkers.domNode, 'headerText');

            statusTable.addChild(this.elapsedTimeHeader);
            statusTable.addChild(this.workerInUse);
            statusTable.addChild(this.optNumWorkers);

            domConstruct.place(statusTable.domNode, headerLeft.containerNode, 'only');

            /*          var openPAText = domConstruct.create("div", {
                                style:{"height": "19px",
                                           "width": "300px",
                                           "font-size": "8pt",
                                           "padding": "2px"},
                                innerHTML: appL10n.OpenPAText
                        }, headerLeft.containerNode); */

            domAttr.set(this.elapsedTimeHeader, 'data-tag', 'build-status.' + this.targetType + '.elapsedTimeHeader');
            domAttr.set(this.workerInUse, 'data-tag', 'build-status.' + this.targetType + '.workerInUse');
            domAttr.set(this.optNumWorkers, 'data-tag', 'build-status.' + this.targetType + '.optNumWorkers');

            // -----------------------------------------------------------------------------
            // create the center pane to hold the build status table
            var cpCenter = new ContentPane({
                region: 'center',
                splitter: true,
                style: 'border:none;height:70%;width:100%;padding-top:20px'
            });
            mainContainer.addChild(cpCenter);
            this.gridNode = cpCenter;

            this.mainContainer = mainContainer;

            this._handleInitUI(initialMsg);
        },

        start: function () {
            MessageService.subscribe(this.updateBuildStatusTableChannel,
                this._handleUpdateBuildStatusTable,
                this);

            MessageService.subscribe(this.topProgressBarChannel,
                this._handleUpdateTopProgressBar,
                this);

            MessageService.subscribe(this.poolUsageChannel,
                this._handleUpdatePoolUsage,
                this);

            MessageService.publish(this.informReadyChannel, 'ready');
        },

        destroy: function () {
            MessageService.unsubscribe(this.updateBuildStatusTableChannel,
                this._handleUpdateBuildStatusTable,
                this);

            MessageService.unsubscribe(this.topProgressBarChannel,
                this._handleUpdateTopProgressBar,
                this);

            MessageService.unsubscribe(this.poolUsageChannel,
                this._handleUpdatePoolUsage,
                this);
        },

        // Initial info about all the mdl refs + top model is received here. Populate the grid data
        _handleInitUI: function (msg) {
            this._createGrid(msg.data.tableData);

            this.numWks = msg.data.numWks;
            domAttr.set(this.workerInUse, 'content', msg.data.numWkInUse.toString() + ' of ' + this.numWks.toString());

            this.numTotalMdls = msg.data.numTotalMdls;
            if (this.numTotalMdls !== 0) {
                var nFinished = msg.data.topPBVal;
                this.topProgressBar.set('value', 100 * nFinished / this.numTotalMdls);
                this.topProgressBar.set('label', nFinished.toString() + ' of ' + this.numTotalMdls.toString());
            }

            domAttr.set(this.elapsedTimeHeader, 'content', '00:00:00');

            if (msg.data.optNumWorkers === 0) {
                domAttr.set(this.optNumWorkers, 'content', appL10n.Pending);
            } else {
                domAttr.set(this.optNumWorkers, 'content', msg.data.optNumWorkers.toString());
            }

            this.elapsedTimeHeader.set('content', msg.data.totalElapsedTime);
        },

        _handleUpdateBuildStatusTable: function (msg) {
            var newData = msg.data;
            var currentData = this._store.get(newData.mdlRefName);

            // update the currentData
            for (var prop in newData) {
                if (currentData.hasOwnProperty(prop)) {
                    currentData[prop] = newData[prop];
                }
            }
            // put the updated currentData into the store and display in the UI
            this._store.put(currentData);
        },

        _handleUpdateTopProgressBar: function (msg) {
            var nFinished = msg.data;
            this.topProgressBar.set('value', 100 * nFinished / this.numTotalMdls);
            this.topProgressBar.set('label', nFinished.toString() + ' of ' + this.numTotalMdls.toString());
        },

        _handleUpdatePoolUsage: function (msg) {
            switch (msg.data.option) {
            case 'updateWorkerInUse':
                domAttr.set(this.workerInUse,
                    'content', msg.data.numWkInUse.toString() + ' of ' + this.numWks.toString());
                break;
            case 'updateOptNumWorkers':
                domAttr.set(this.optNumWorkers, 'content', msg.data.optNumWorkers.toString());
                break;
            case 'ctrlTotalElapsedTimer':
                if (msg.data.control === 'startTimer') {
                    lang.hitch(this, '_startElapsedTimeTimer', 0)();
                } else if (msg.data.control === 'stopTimer') {
                    this._stopTimer = true;
                }
                break;
            }
        },

        _formatProgress: function (progress) {
            var w = new ProgressBar();
            if (progress > 100) {
                progress = 100;
            }
            w.set('value', progress);
            w._destroyOnRemove = true;
            return w;
        },

        _createGrid: function (dataItems) {
            // convert dataItems to an array if it is not. dataItems is not an array when there
            // is only one model in the table, which may happen in future with sequential build
            // or future improved parallel build.
            if (!(dataItems instanceof Array)) {
                dataItems = [dataItems];
            }

            if (dataItems.length > 0) {
                this._store = new Observable(new Memory({
                    data: dataItems,
                    idProperty: 'mdlRefName'
                }));

                // set up layout
                var layout = [
                    { 'label': appL10n.ModelName, 'field': 'mdlRefName' },
                    { 'label': appL10n.Status, 'field': 'status' },
                    { 'label': appL10n.ElapsedTime, 'field': 'buildTime' }
                ];

                var grid = new ModelStatusList({
                    pagingMethod: 'throttleDelayed',
                    store: this._store,
                    'class': 'modelStatus-list',
                    'sort': 'mdlRefName',
                    columns: layout
                });
                grid.set('data-tag', 'build-status.modelStatusTable');
                domConstruct.place(grid.domNode, this.gridNode.containerNode, 'only');
                grid.startup(); // Call startup() to render the grid
            } else {
                this.gridNode.set('content', appL10n.ModelStatusListNoBuildStatus);
                // this.gridNode.set("style", "font-weight: bold");
            }
        },

        _startElapsedTimeTimer: function (offset) {
            // setup duration timer
            var startTime = new Date().getTime();
            if (this._timerId) {
                clearInterval(this._timerId);
            }

            this._timerId = setInterval(lang.hitch(this, function () {
                if (this._stopTimer) {
                    // Stop timer when the build is completed, errored out or canceled.
                    clearInterval(this._timerId);
                }
                var elapsed =
                    offset + (new Date().getTime() - startTime) / 1000;

                this.elapsedTimeHeader.set('content', this._formatTime(elapsed));
            }), 1000);
        },

        _formatTime: function (time) {
            var sec_num;
            sec_num = Math.round(time);

            if (isNaN(sec_num) || sec_num < 0) {
                sec_num = 0;
            }
            var hours = Math.floor(sec_num / 3600);
            var minutes = Math.floor(sec_num / 60) % 60;
            var seconds = sec_num % 60;

            if (hours < 10) {
                hours = '0' + hours;
            }
            if (minutes < 10) {
                minutes = '0' + minutes;
            }
            if (seconds < 10) {
                seconds = '0' + seconds;
            }

            return hours + ':' + minutes + ':' + seconds;
        }

    });

    return BuildStatusInfo;
});
